/**
 * Knowledge wave K1: the employee and manager assistant's wire shapes, exactly
 * as docs/product/modules/knowledge/2026-10-04-k1-employee-assistant.md writes
 * them ("Portal API", "SSE events", "Admin try it").
 *
 * Kept apart from lib/knowledge/types.ts (the K0 admin contract, owned by the
 * admin screens) so the two lanes never edit one file. The two vocabularies the
 * files share (kind, surface) are the same lists as the API's
 * apps/api/src/database/schema/knowledge.ts.
 *
 * Knowledge wave K2 (docs/product/modules/knowledge/2026-10-04-k2-team-assistant.md)
 * added, additively: team citations (`type: 'team'`), and on the manager
 * assistant the team data it may read (`AssistantPublic.team`).
 */

export const KNOWLEDGE_PORTAL_SURFACES = ['employee', 'manager'] as const
export type KnowledgePortalSurface = (typeof KNOWLEDGE_PORTAL_SURFACES)[number]

export const KNOWLEDGE_PORTAL_KINDS = ['policy', 'procedure', 'standard', 'guideline', 'faq', 'other'] as const
export type KnowledgePortalKind = (typeof KNOWLEDGE_PORTAL_KINDS)[number]

export type KnowledgeAnswerMode = 'documents_only' | 'documents_first'

export interface KnowledgeEscalation {
  label: string | null
  email: string | null
  url: string | null
}

/** One surface's assistant as a person sees it (GET /v1/portal/knowledge/assistant). */
export interface AssistantPublic {
  surface: KnowledgePortalSurface
  /** The admin's name, or the default name in the person's locale. */
  name: string
  greeting: string | null
  suggestedQuestions: string[]
  answerMode: KnowledgeAnswerMode
  escalation: KnowledgeEscalation
  dailyLimit: number
  remainingToday: number
  /**
   * K2: on the manager assistant, the team data it may read now (each dataset
   * only when both the assistant's switch and the manager view's own are on);
   * null on the employee assistant and when team data is off.
   */
  team: TeamAvailability | null
}

/** What the manager's assistant may read about their own team. */
export interface TeamAvailability {
  training: boolean
  simulations: boolean
  risk: boolean
  names: boolean
  draftReminders: boolean
}

export interface AssistantAvailability {
  employee: AssistantPublic | null
  manager: AssistantPublic | null
}

/**
 * A document an answer cites: the marker `[S1]` in the text names it. K2 added
 * `type: 'document'`; a message stored before K2 has none and is a document.
 */
export interface DocumentCitation {
  marker: string
  type?: 'document'
  sourceId: string
  title: string
  kind: KnowledgePortalKind
  pageFrom: number | null
  pageTo: number | null
  heading: string | null
}

export const KNOWLEDGE_TEAM_DATASETS = ['overview', 'training', 'simulations', 'risk'] as const
export type KnowledgeTeamDataset = (typeof KNOWLEDGE_TEAM_DATASETS)[number]

/**
 * K2: team data an answer cites — the marker `[T1]` names it. It opens
 * nothing; its chip reads "Team · Training status · as of 4 Oct".
 */
export interface TeamCitation {
  marker: string
  type: 'team'
  dataset: KnowledgeTeamDataset
  /** When the team data was read: an instant, ISO. */
  asOf: string
  /** The workspace zone the day of `asOf` is read in. */
  timeZone: string
}

/** What an answer cites. */
export type Citation = DocumentCitation | TeamCitation

export const isTeamCitation = (citation: Citation): citation is TeamCitation => citation.type === 'team'

export const KNOWLEDGE_ANSWER_OUTCOMES = ['answered', 'not_found', 'limited', 'error'] as const
export type KnowledgeAnswerOutcome = (typeof KNOWLEDGE_ANSWER_OUTCOMES)[number]

export interface ConversationSummary {
  id: string
  title: string | null
  lastMessageAt: string
}

export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations: Citation[]
  outcome: KnowledgeAnswerOutcome | null
  createdAt: string
}

export interface ConversationDetail {
  id: string
  surface: KnowledgePortalSurface
  title: string | null
  messages: ConversationMessage[]
}

export interface LibraryItem {
  id: string
  title: string
  description: string | null
  kind: KnowledgePortalKind
  language: string | null
  versionLabel: string | null
  /** A calendar day, yyyy-MM-dd. */
  effectiveOn: string | null
  /** A calendar day, yyyy-MM-dd. */
  reviewOn: string | null
  ownerName: string | null
  pageCount: number | null
  /** An instant, ISO. */
  updatedAt: string
  file: { name: string; mime: string; sizeBytes: number }
}

export interface LibrarySearchHit {
  sourceId: string
  title: string
  kind: KnowledgePortalKind
  pageFrom: number | null
  pageTo: number | null
  heading: string | null
  snippet: string
}

export interface LibrarySearchResult {
  mode: 'hybrid' | 'keyword'
  items: LibrarySearchHit[]
}

/** The stream's `error` codes (and the HTTP ones the web also maps). */
export const KNOWLEDGE_ASK_ERROR_CODES = [
  'knowledge_assistant_disabled',
  'knowledge_daily_limit_reached',
  'knowledge_question_too_long',
  'knowledge_ai_unavailable',
  'knowledge_conversation_not_found',
] as const

/** One frame of POST /ask (or /try): `data: {"type": …, …payload}`. */
export type AskEvent =
  | { type: 'conversation'; id: string; title: string | null }
  | { type: 'sources'; items: Citation[] }
  | { type: 'text'; delta: string }
  | {
      type: 'done'
      messageId: string | null
      outcome: KnowledgeAnswerOutcome
      /** The FINAL sanitised answer: it replaces the streamed text. */
      content: string
      /** Only the sources the answer actually cites. */
      citations: Citation[]
      /** null on the admin try route (admins have no daily counter). */
      remainingToday: number | null
    }
  | { type: 'error'; errorCode: string }

export interface AskRequest {
  question: string
  conversationId?: string
  surface: KnowledgePortalSurface
  locale: string
}

export interface TryRequest {
  question: string
  surface: KnowledgePortalSurface
  locale: string
}

/** The question's length limit after trimming (the API's DTO). */
export const KNOWLEDGE_QUESTION_MAX = 1000
